import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  doc, serverTimestamp, writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { db, storage } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { getNicheMeta, NICHE_OPTIONS } from "../components/niche/nicheMeta";
import { getNicheThemeStyle } from "../utils/nicheTheme";
import { checkUsernameAvailable, releaseUsername, reserveUsername } from "../services/usernameService";
import { fetchUserByHandle } from "../services/userLookupService";
import CreatorStudioLayout from "../pages/creator/CreatorStudioLayout";

import "./CreatorProfileEdit.css";

const BIO_MAX = 160;

export default function CreatorProfileEdit() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [displayName, setDisplayName] = useState("");
  const [handleValue, setHandleValue] = useState("");
  const [originalHandle, setOriginalHandle] = useState("");
  const [bio, setBio] = useState("");
  const [niche, setNiche] = useState("independent");
  const [location, setLocation] = useState("");
  const [instagram, setInstagram] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [youtube, setYoutube] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const [bannerPreview, setBannerPreview] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [handleStatus, setHandleStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const nicheMeta = getNicheMeta(niche);
  const nicheTheme = getNicheThemeStyle(nicheMeta.key);

  useEffect(() => {
    if (!currentUser || !handle) return;
    const load = async () => {
      const creator = await fetchUserByHandle(handle);
      if (!creator || creator.uid !== currentUser.uid) {
        setAuthorized(false);
        setLoaded(true);
        return;
      }
      setAuthorized(true);
      setDisplayName(creator.displayName || "");
      const h = creator.handle || "";
      setHandleValue(h);
      setOriginalHandle(h);
      setBio(creator.bio || "");
      setNiche(getNicheMeta(creator.niche).key);
      setLocation(creator.location || "");
      setInstagram(creator.social?.instagram || "");
      setTiktok(creator.social?.tiktok || "");
      setYoutube(creator.social?.youtube || "");
      setAvatarPreview(creator.avatar || creator.avatarUrl || "");
      setBannerPreview(creator.bannerImage || creator.bannerUrl || "");
      setLoaded(true);
    };
    load();
  }, [currentUser, handle]);

  useEffect(() => {
    if (!handleValue || handleValue === originalHandle) {
      setHandleStatus("");
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const available = await checkUsernameAvailable(handleValue, currentUser.uid);
        setHandleStatus(available ? "available" : "taken");
      } catch {
        setHandleStatus("taken");
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [handleValue, originalHandle, currentUser?.uid]);

  if (!currentUser) return <Navigate to="/welcome?auth=signin" replace />;
  if (loaded && !authorized) return <Navigate to={`/creator/${handle}`} replace />;

  const showToast = (text, type = "success") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  const uploadToStorage = async (file, folder) => {
    const path = `${folder}/${currentUser.uid}/${Date.now()}-${file.name.replace(/\s+/g, "-")}`;
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file);
    return getDownloadURL(fileRef);
  };

  const handleSave = async () => {
    if (bio.length > BIO_MAX) {
      showToast(`Bio must be ${BIO_MAX} characters or less`, "error");
      return;
    }
    if (handleStatus === "taken") {
      showToast("Handle is already taken", "error");
      return;
    }

    setSaving(true);
    try {
      let avatarUrl = avatarPreview;
      let bannerUrl = bannerPreview;

      if (avatarFile) avatarUrl = await uploadToStorage(avatarFile, "avatars");
      if (bannerFile) bannerUrl = await uploadToStorage(bannerFile, "banners");

      let cleanedHandle = handleValue.trim().toLowerCase().replace(/[^a-z0-9._]/g, "");
      if (cleanedHandle !== originalHandle) {
        cleanedHandle = await reserveUsername({ username: cleanedHandle, uid: currentUser.uid });
        if (originalHandle) await releaseUsername(originalHandle);
      }

      const batch = writeBatch(db);
      const userRef = doc(db, "users", currentUser.uid);
      batch.update(userRef, {
        displayName: displayName.trim(),
        handle: cleanedHandle,
        username: cleanedHandle,
        bio: bio.trim(),
        niche,
        location: location.trim(),
        avatar: avatarUrl,
        avatarUrl,
        bannerImage: bannerUrl,
        bannerUrl,
        social: {
          instagram: instagram.trim(),
          tiktok: tiktok.trim(),
          youtube: youtube.trim(),
        },
        updatedAt: serverTimestamp(),
      });
      await batch.commit();

      showToast("Profile saved!");
      setTimeout(() => navigate(`/creator/${cleanedHandle}`), 800);
    } catch (err) {
      console.error("Save error:", err);
      showToast(err.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <CreatorStudioLayout>
        <div className="cs-loading">Loading…</div>
      </CreatorStudioLayout>
    );
  }

  return (
    <CreatorStudioLayout activePath={`/creator/${handle}/edit`}>
        <div className="cpe-main" style={nicheTheme}>
          <h1 className="cpe-section-title">Edit your <em>profile</em></h1>
          <span className="cpe-section-hand">how fans discover and follow you</span>

          <div className="cpe-card">
            <div className="cpe-card-head"><div><div className="cpe-card-title">Identity</div><div className="cpe-card-sub">Name, handle, and bio</div></div></div>
            <div className="cpe-field-row">
              <div className="cpe-field">
                <label className="cpe-field-label">Display name</label>
                <input className="cpe-field-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div className="cpe-field">
                <label className="cpe-field-label">Handle</label>
                <input className="cpe-field-input" value={handleValue} onChange={(e) => setHandleValue(e.target.value.replace(/[^a-zA-Z0-9._]/g, ""))} />
                {handleStatus === "available" && <span className="cpe-field-hint cpe-field-hint--good">Available</span>}
                {handleStatus === "taken" && <span className="cpe-field-hint cpe-field-hint--warn">Already taken</span>}
              </div>
            </div>
            <div className="cpe-field cpe-field--single">
              <label className="cpe-field-label">Bio</label>
              <textarea className="cpe-field-input" rows={3} maxLength={BIO_MAX} value={bio} onChange={(e) => setBio(e.target.value)} />
              <span className={`cpe-char-count${bio.length > BIO_MAX * 0.9 ? " cpe-char-count--warn" : ""}`}>{bio.length}/{BIO_MAX}</span>
            </div>
          </div>

          <div className="cpe-card">
            <div className="cpe-card-head"><div><div className="cpe-card-title">Photos</div><div className="cpe-card-sub">Avatar and banner</div></div></div>
            <div className="cpe-photo-row">
              <div className="cpe-photo-block">
                <label className="cpe-field-label">Avatar</label>
                <button type="button" className="cpe-upload-zone" onClick={() => avatarInputRef.current?.click()}>
                  {avatarPreview ? <img src={avatarPreview} alt="Avatar preview" className="cpe-upload-preview" /> : "Upload avatar"}
                </button>
                <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)); }
                }} />
              </div>
              <div className="cpe-photo-block cpe-photo-block--banner">
                <label className="cpe-field-label">Banner</label>
                <button type="button" className="cpe-banner-zone" onClick={() => bannerInputRef.current?.click()}>
                  {bannerPreview ? <img src={bannerPreview} alt="Banner preview" className="cpe-banner-preview" /> : "Upload banner"}
                </button>
                <input ref={bannerInputRef} type="file" accept="image/*" hidden onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setBannerFile(f); setBannerPreview(URL.createObjectURL(f)); }
                }} />
              </div>
            </div>
          </div>

          <div className="cpe-card">
            <div className="cpe-card-head"><div><div className="cpe-card-title">Niche & location</div></div></div>
            <div className="cpe-field-row">
              <div className="cpe-field">
                <label className="cpe-field-label">Niche</label>
                <select className="cpe-field-select" value={niche} onChange={(e) => setNiche(e.target.value)}>
                  {NICHE_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="cpe-field">
                <label className="cpe-field-label">Location</label>
                <input className="cpe-field-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Toronto, ON" />
              </div>
            </div>
          </div>

          <div className="cpe-card">
            <div className="cpe-card-head"><div><div className="cpe-card-title">Social links</div></div></div>
            <div className="cpe-field-row cpe-field-row--triple">
              <div className="cpe-field">
                <label className="cpe-field-label">Instagram</label>
                <input className="cpe-field-input" value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@handle" />
              </div>
              <div className="cpe-field">
                <label className="cpe-field-label">TikTok</label>
                <input className="cpe-field-input" value={tiktok} onChange={(e) => setTiktok(e.target.value)} placeholder="@handle" />
              </div>
              <div className="cpe-field">
                <label className="cpe-field-label">YouTube</label>
                <input className="cpe-field-input" value={youtube} onChange={(e) => setYoutube(e.target.value)} placeholder="channel or URL" />
              </div>
            </div>
          </div>

          <div className="cpe-action-row">
            <button type="button" className="cpe-btn-cancel" onClick={() => navigate(`/creator/${handle}`)}>Cancel</button>
            <button type="button" className="cpe-btn-save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

      {toast && <div className={`cpe-toast cpe-toast--${toast.type}`}>{toast.text}</div>}
    </CreatorStudioLayout>
  );
}
